import { gql, useMutation } from "@apollo/client";

import {
  CURRENT_USER_QUERY,
  LINK_EMAIL_ACCOUNT_MUTATION,
  UNLINK_EMAIL_ACCOUNT_MUTATION,
  MOVE_EMAIL_TO_FOLDER_MUTATION,
  SEND_EMAIL_MUTATION,
} from "~/api";

export const GET_EMAIL_THREAD_QUERY = gql`
  query getEmailThread(
    $scope: String!
    $conversationId: String!
    $emailAddressId: ID!
  ) {
    getEmailThread(
      scope: $scope
      conversationId: $conversationId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      participants {
        name
        email
      }
      messages {
        id
        createdDateTime
        receivedDateTime
        sentDateTime
        hasAttachments
        subject
        bodyPreview
        parentFolderId
        conversationId
        isRead
        isDraft
        body {
          contentType
          content
        }
        from {
          name
          email
        }
        toRecipients {
          name
          email
        }
        ccRecipients {
          name
          email
        }
        bccRecipients {
          name
          email
        }
        replyTo {
          name
          email
        }
      }
      linkedTickets {
        _id
        ticketId
      }
      linkedSupportTickets {
        _id
        supportTicketId
        title
      }
    }
  }
`;

// A personal-mailbox thread linked to one of the company's work orders or
// tickets. Read with the mailbox owner's token, after the backend checks the
// caller may open that work order or ticket.
export const GET_COMPANY_EMAIL_THREAD_QUERY = gql`
  query getCompanyEmailThread(
    $companyId: ID!
    $conversationId: String!
    $emailAddressId: ID!
  ) {
    getCompanyEmailThread(
      companyId: $companyId
      conversationId: $conversationId
      emailAddressId: $emailAddressId
    ) {
      id
      subject
      hasAttachments
      snippet
      unread
      messageIds
      date
      earliestMessageDate
      latestMessageSentDate
      latestMessageReceivedDate
      participants {
        name
        email
      }
      messages {
        id
        createdDateTime
        receivedDateTime
        sentDateTime
        hasAttachments
        subject
        bodyPreview
        parentFolderId
        conversationId
        isRead
        isDraft
        body {
          contentType
          content
        }
        from {
          name
          email
        }
        toRecipients {
          name
          email
        }
        ccRecipients {
          name
          email
        }
        bccRecipients {
          name
          email
        }
        replyTo {
          name
          email
        }
      }
      accountId
      accountEmail
      accountType
      canReply
    }
  }
`;

export const LIST_EMAIL_FOLDERS_QUERY = gql`
  query listEmailFolders(
    $scope: String!
    $pageToken: String
    $emailAddressId: ID!
  ) {
    listEmailFolders(
      scope: $scope
      pageToken: $pageToken
      emailAddressId: $emailAddressId
    ) {
      pageToken
      folders {
        id
        displayName
        attributes
        parentFolderId
        childFolderCount
        unreadItemCount
        totalItemCount
        isHidden
      }
    }
  }
`;
import client from "~/apollo/_client";

const updateOemCache = (cache, ref) => {
  const currentUserCache = cache.readQuery({
    query: CURRENT_USER_QUERY,
    variables: {},
  });
  if (!currentUserCache) return;

  const cloned = JSON.parse(JSON.stringify(currentUserCache.currentUser));
  cloned.oem = {
    ...cloned.oem,
    emailAccounts: ref.accounts,
    maximumAllowedEmailAddresses: ref.maximumAllowedEmailAccounts,
  };

  cache.writeQuery({
    query: CURRENT_USER_QUERY,
    variables: {},
    data: { currentUser: cloned },
  });
};

const updatePersonalCache = (cache, ref) => {
  const currentUserCache = cache.readQuery({
    query: CURRENT_USER_QUERY,
    variables: {},
  });
  if (!currentUserCache) return;

  const cloned = JSON.parse(JSON.stringify(currentUserCache.currentUser));
  cloned.personalEmailAccounts = ref.accounts;
  cloned.personalMaximumAllowedEmailAddresses = ref.maximumAllowedEmailAccounts;

  cache.writeQuery({
    query: CURRENT_USER_QUERY,
    variables: {},
    data: { currentUser: cloned },
  });
};

export const useLinkEmailAccount = (scope = "oem") => {
  const [link, { loading, error }] = useMutation(LINK_EMAIL_ACCOUNT_MUTATION);

  return {
    loading,
    error,
    linkEmail: async (code) =>
      link({
        variables: {
          code,
          scope,
        },
        update(cache, { data: { linkEmailAccount: ref } }) {
          try {
            if (scope === "personal") {
              updatePersonalCache(cache, ref);
            } else {
              updateOemCache(cache, ref);
            }
          } catch (_) {
            //
          }
        },
      }),

    handleEmailAccountSync: ({ payload }) => {
      const { currentUser } =
        client.cache.readQuery({
          query: CURRENT_USER_QUERY,
        }) || {};

      const emailConfigs = currentUser.oem.emailAccounts?.map((emailAccount) =>
        emailAccount._id === payload?.emailAccountId
          ? { ...emailAccount, isSynced: true }
          : { ...emailAccount, isSynced: !!emailAccount.isSynced },
      );

      client.cache.writeQuery({
        query: CURRENT_USER_QUERY,
        data: {
          currentUser: {
            ...currentUser,
            oem: {
              ...currentUser.oem,
              emailAccounts: emailConfigs,
            },
          },
        },
      });
    },
  };
};

export const useUnlinkEmailAccount = (scope = "oem") => {
  const [unlink, { loading, error }] = useMutation(
    UNLINK_EMAIL_ACCOUNT_MUTATION,
  );

  return {
    loading,
    error,
    unlinkEmail: async (emailAddressId) =>
      unlink({
        variables: {
          emailAddressId,
          scope,
        },
        update(cache, { data: { unlinkEmailAccount: ref } }) {
          try {
            if (scope === "personal") {
              updatePersonalCache(cache, ref);
            } else {
              updateOemCache(cache, ref);
            }
          } catch (_) {
            //
          }
        },
      }),
  };
};

export const useMoveEmailToFolder = (scope = "oem") => {
  const [moveEmail, { loading, error }] = useMutation(
    MOVE_EMAIL_TO_FOLDER_MUTATION,
  );

  return {
    loading,
    error,
    moveEmailToFolder: async (messageIds, folderId, emailAddressId) =>
      moveEmail({
        variables: { scope, messageIds, folderId, emailAddressId },
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listEmailThreadsByFolder",
          });
          cache.gc();
        },
      }),
  };
};

export const useSendEmail = (scope = "oem") => {
  const [send, { loading, error }] = useMutation(SEND_EMAIL_MUTATION);

  return {
    loading,
    error,
    sendEmail: async (input) =>
      send({
        variables: { scope, input },
      }),

    handleUpdateEmailCache: ({ payload }) => {
      if (payload?.replied) {
        const { getEmailThread } = client.cache.readQuery({
          query: GET_EMAIL_THREAD_QUERY,
          variables: {
            threadId: payload?.threadId,
            emailAddressId: payload?.accountId,
          },
        });
        const ticketId = getEmailThread?.ticket?._id;
        if (
          window.location.pathname.includes(payload?.threadId) ||
          (ticketId && window.location.pathname.includes(ticketId))
        ) {
          client.cache.writeQuery({
            query: GET_EMAIL_THREAD_QUERY,
            variables: {
              threadId: payload?.threadId,
              emailAddressId: payload?.accountId,
            },
            data: {
              getEmailThread: {
                ...getEmailThread,
                thread: {
                  ...getEmailThread.thread,
                  messageIds: [
                    ...getEmailThread.thread.messageIds,
                    payload?.message?.id,
                  ],
                  messages: [
                    { ...payload?.message, replyTo: [], unread: false },
                    ...getEmailThread.thread.messages,
                  ],
                },
              },
            },
          });
        }
      } else {
        client.cache.evict({
          id: "ROOT_QUERY",
          fieldName: "listEmailThreadsByTicket",
        });

        client.cache.evict({
          id: "ROOT_QUERY",
          fieldName: "listEmailThreadsByFolder",
        });

        client.cache.evict({
          id: "ROOT_QUERY",
          fieldName: "listDrafts",
        });

        client.cache.evict({
          id: "ROOT_QUERY",
          fieldName: "getDraftAndJunkEmailCount",
        });

        client.cache.gc();
      }
    },
  };
};

export const updateFolderUnreadCount = (folderToCountMap, emailAddressId) => {
  const folders = client.readQuery({
    query: LIST_EMAIL_FOLDERS_QUERY,
    variables: {
      params: {
        limit: 100,
        skip: 0,
        where: {
          emailAddressId,
        },
      },
    },
  });

  const updatedFolders = folders.listEmailFolders.folders.map((folder) => ({
    ...folder,
    unreadCount: folderToCountMap[folder.id],
  }));

  client.writeQuery({
    query: LIST_EMAIL_FOLDERS_QUERY,
    variables: {
      params: {
        limit: 100,
        skip: 0,
        where: {
          emailAddressId,
        },
      },
    },
    data: {
      listEmailFolders: { folders: updatedFolders },
    },
  });
};
